import { PeakRealTime, PeakDetectorParams } from "@hyfe/peak-detection-wasm";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";

export type PeakWithSamples = PeakRealTime & { samples: Float32Array };

type PeakDetectorCallbackData = {
  peaks: PeakWithSamples[];
  startTimestamp: number;
  endTimestamp: number;
};
export type PeakDetectorCallback = (data: PeakDetectorCallbackData) => void;

export const usePeakDetectionWorker = (
  params: Partial<PeakDetectorParams>,
  callback: PeakDetectorCallback
) => {
  const worker = useRef<Worker>();

  // Deep comparison check of the peak detect params
  // To avoid unnecessary changes
  const [_params, _setParams] = useState(params);
  useEffect(() => {
    if (isEqual(params, _params)) {
      _setParams(params);
    }
  }, [params]);

  const initWorker = () => {
    worker.current = new Worker(`PEAK_DETECTION_WORKER_URL`);
  };
  const stopWorker = () => {
    if (worker.current) {
      worker.current.terminate();
    }
  };

  useEffect(() => {
    initWorker();
    return stopWorker;
  }, []);

  const detectPeaks = useCallback(
    (samples: Float32Array, ts: number) => {
      if (worker.current)
        worker.current.postMessage({
          action: "detect",
          payload: { samples, params: _params, ts },
        });
    },
    [_params]
  );

  useEffect(() => {
    if (worker.current) worker.current.onmessage = (ev) => callback(ev.data);
  }, [callback]);

  return { detectPeaks };
};

export default usePeakDetectionWorker;
